import React, { useEffect, useState } from "react";
import { X, Star, ChevronDown } from "lucide-react";
import { handleOutsideClick } from "../../../Helpers/HandleOutsideClick";

const PreviousFeedbackModal = ({ show, onClose, feedbacks }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);
  const [openRounds, setOpenRounds] = useState({});

  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "auto";

    if (show) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      setTimeout(() => setShouldRender(false), 500);
    }

    return () => (document.body.style.overflow = "auto");
  }, [show]);

  useEffect(() => {
    if (feedbacks?.length > 0) {
      setOpenRounds({ 0: true });
    }
  }, [feedbacks]);

  const toggleRound = (index) => {
    setOpenRounds((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (!shouldRender) return null;

  return (
    <div 
      className={`fixed inset-0 z-[90] flex justify-end transition-all duration-500
      ${isVisible ? "bg-black/40 opacity-100" : "bg-black/0 opacity-0"}`}
      onClick={(e) => handleOutsideClick(e, onClose)}
    >
      {/* Drawer */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`
          bg-white h-full w-[45%] min-w-[420px]
          shadow-[-10px_0px_30px_rgba(0,0,0,0.2)]
          flex flex-col
          transform transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${isVisible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Previous Interview Feedback</h2>

          <button onClick={onClose} className="hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50">
          {feedbacks?.length === 0 && (
            <div className="text-center text-gray-500">
              No previous feedback available
            </div>
          )}

          {/* Loop Rounds */}
          {feedbacks?.map((round, index) => {
            const isOpen = openRounds[index];

            return (
              <div
                key={index}
                className={`rounded-xl border transition-all duration-300 ${
                  isOpen
                    ? "bg-white shadow-lg border-blue-200"
                    : "bg-white shadow-md border-gray-200"
                }`}
              >
                <div
                  onClick={() => toggleRound(index)}
                  className="bg-blue-50 px-4 py-3 font-semibold text-blue-700 border-b flex justify-between items-center cursor-pointer hover:bg-blue-100 transition"
                >
                  <span>Round {index + 1}</span>

                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </div>

                <div
                  className={`transition-all duration-300 origin-top ${
                    isOpen
                      ? "opacity-100 scale-y-100 p-4 space-y-4"
                      : "opacity-0 scale-y-95 h-0 overflow-hidden p-0"
                  }`}
                >
                  {round.questions?.map((q, i) => (
                    <div key={i} className="border-b pb-3 last:border-none">
                      <p className="font-medium text-gray-800">{q.question}</p>

                      {/* Rating */}
                      {q.rating && (
                        <div className="flex gap-1 mt-1">
                          {[1, 2, 3, 4].map((star) => (
                            <Star
                              key={star}
                              size={16}
                              className={
                                q.rating >= star
                                  ? "text-yellow-400 fill-yellow-400"
                                  : "text-gray-300"
                              }
                            />
                          ))}
                        </div>
                      )}

                      {/* Remark */}
                      <p className="text-sm text-gray-600 mt-1">
                        Remark:{" "}
                        <span className="font-medium text-gray-800">
                          {q.remark || "N/A"}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviousFeedbackModal;
